import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { Spinner } from 'react-bootstrap'
import { CompanyPageAccordion } from 'components/NewCompanyPage/CompanyPageAccordion'
import apiService from 'services/api'
import { ReminderListItem } from 'models/reminders'
import { MobileReminderItem } from './MobileReminderItem'
import { PiCalendarStar } from 'react-icons/pi'

interface MobileRemindersAccordionProps {
  chainProxyId: number
  doorProxyId?: number
}

interface GroupedReminders {
  oneTime: ReminderListItem[]
  recurring: ReminderListItem[]
}

export function MobileRemindersAccordion({
  chainProxyId,
  doorProxyId,
}: MobileRemindersAccordionProps) {
  const api = apiService()
  const navigate = useNavigate()

  const handleViewAllClick = () => {
    const params = new URLSearchParams()
    params.set('contact_company_id', String(chainProxyId))
    if (doorProxyId) {
      params.set('door_proxy_id', String(doorProxyId))
    }
    navigate(`/reminders?${params.toString()}`)
  }

  // Fetch upcoming reminders for this company/door
  const { data, isLoading, error } = useQuery({
    queryKey: ['mobileReminders', chainProxyId, doorProxyId],
    queryFn: async ({ signal }) => {
      const params: Record<string, unknown> = {
        page: 1,
        page_size: 20,
        contact_company_id: chainProxyId,
        status: 'scheduled', // Only show scheduled (upcoming) reminders
      }
      if (doorProxyId) {
        params.door_proxy_id = doorProxyId
      }
      return api.getReminders(params, signal)
    },
    enabled: !!chainProxyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  // Group reminders by type (one-time vs recurring)
  const groupedReminders = useMemo<GroupedReminders>(() => {
    if (!data?.results) return { oneTime: [], recurring: [] }

    const oneTime: ReminderListItem[] = []
    const recurring: ReminderListItem[] = []

    data.results.forEach((reminder) => {
      if (reminder.reminder_type === 'one_off') {
        oneTime.push(reminder)
      } else {
        recurring.push(reminder)
      }
    })

    // Sort by next_run_at
    const sortByDate = (a: ReminderListItem, b: ReminderListItem) => {
      const dateA = new Date(a.next_run_at || a.run_at || 0).getTime()
      const dateB = new Date(b.next_run_at || b.run_at || 0).getTime()
      return dateA - dateB
    }

    return {
      oneTime: oneTime.sort(sortByDate),
      recurring: recurring.sort(sortByDate),
    }
  }, [data?.results])

  const hasReminders =
    groupedReminders.oneTime.length > 0 || groupedReminders.recurring.length > 0

  // Custom header with Calendar icon
  const headerLeft = (
    <div className="flex items-center gap-1">
      <PiCalendarStar size={14} className="text-gray-500" />
      <span className="font-light text-uppercase text-sm">UPCOMING</span>
      <span className="text-gray-500 text-sm">(Reminders)</span>
    </div>
  )

  const headerRight = (
    <div
      onClick={(e) => {
        e.stopPropagation()
        handleViewAllClick()
      }}
      className="flex items-center gap-1 text-fb-dark-green text-sm cursor-pointer hover:underline"
    >
      <span>View All</span>
      <ChevronRight className="w-4 h-4" />
    </div>
  )

  return (
    <CompanyPageAccordion
      title=""
      hideChevron={true}
      customTitle={headerLeft}
      headerRight={headerRight}
      defaultOpen={true}
      stickyHeader={false}
      isMobile
    >
      <div className="relative min-h-[80px] max-h-[300px] overflow-y-auto">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {/* Error state */}
        {!!error && (
          <div className="py-8 text-center text-sm text-red-500">
            Failed to load reminders
          </div>
        )}

        {/* Empty state */}
        {!error && !hasReminders && !isLoading && (
          <div className="py-8 text-center text-sm text-gray-500">
            No upcoming reminders
          </div>
        )}

        {/* Content */}
        {!error && hasReminders && (
          <div className="space-y-4">
            {/* One-time reminders */}
            {groupedReminders.oneTime.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-1">
                  ONE-TIME
                </div>
                <div className="space-y-0">
                  {groupedReminders.oneTime.map((reminder) => (
                    <MobileReminderItem key={reminder.id} reminder={reminder} />
                  ))}
                </div>
              </div>
            )}

            {/* Recurring reminders */}
            {groupedReminders.recurring.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100 pb-1">
                  RECURRING
                </div>
                <div className="space-y-0">
                  {groupedReminders.recurring.map((reminder) => (
                    <MobileReminderItem key={reminder.id} reminder={reminder} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </CompanyPageAccordion>
  )
}
